// P1-3: an audible cue for a refused scan.
//
// Why this exists at all: the scan gun beeps when it successfully READS a barcode,
// regardless of what the app then decides. So a driver working a pallet -- looking at
// the equipment, not the laptop -- hears the same confirming beep whether the scan was
// collected or refused as a duplicate. The screen notice is unmissable if you are
// looking at the screen; this is for when you aren't.
//
// WebAudio rather than an <audio> file: no asset to ship or cache, no autoplay policy
// to fight (the context is created inside a keydown/click handler, which is the user
// gesture browsers require), and it works offline, which matters for P1-7.
//
// Deliberately conservative: a short two-tone descending blip, low volume, wrapped so
// that any failure is silent. A thrown exception here must never break a scan.

let audioContext = null;

const getContext = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (Ctor == null) {
        return null;
    }
    if (audioContext == null) {
        audioContext = new Ctor();
    }
    return audioContext;
};

const blip = (ctx, frequency, startAt, duration) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'square';
    oscillator.frequency.value = frequency;

    // Ramped rather than switched, so it reads as a tone instead of a click.
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(0.12, startAt + 0.01);
    gain.gain.linearRampToValueAtTime(0, startAt + duration);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration);
};

/**
 * A short descending two-tone "refused" sound. Distinct from the gun's own single
 * rising confirmation beep, so the two are not mistaken for each other.
 *
 * Never throws: an unavailable or suspended audio context just means no sound.
 */
const playRejectTone = () => {
    try {
        const ctx = getContext();
        if (ctx == null) {
            return false;
        }
        // Safari/Edge can hand back a suspended context on first use.
        if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
            ctx.resume();
        }
        const now = ctx.currentTime;
        blip(ctx, 660, now, 0.12);
        blip(ctx, 440, now + 0.14, 0.18);
        return true;
    } catch (error) {
        // A silent app is a far better outcome than a scan that throws.
        return false;
    }
};

const exports = { playRejectTone };

export default exports;
export { playRejectTone };
